/**
 * Cosmetic definitions.
 *
 * Every cosmetic is a small drawing described by data: a `pattern` and a
 * `tint`, plus a `glyph` for the ones that carry a mark. The renderer in
 * `src/ui/store/CosmeticSwatch.tsx` switches on `pattern`, which is a design
 * vocabulary, never on the cosmetic's id. Adding a sleeve is one JSON entry.
 *
 * Tints name a design token (`btc`, `eth`, `accent`, `neutral`) or the word
 * `prism` for the Genesis gradient, so cosmetics cannot introduce a colour the
 * token file does not already have.
 */

import rawCosmetics from '@/data/cosmetics.json';

export type CosmeticKind = 'sleeve' | 'playmat' | 'avatar' | 'banner' | 'misc';

export type CosmeticPattern =
  | 'grid'
  | 'hex'
  | 'traces'
  | 'speed'
  | 'honeycomb'
  | 'rings'
  | 'orbit'
  | 'prism'
  | 'bolt'
  | 'glyph';

export interface Cosmetic {
  id: string;
  kind: CosmeticKind;
  name: string;
  blurb: string;
  price: number;
  /** The free one every account starts with, one per kind. */
  default?: boolean;
  pattern: CosmeticPattern;
  /** A faction id, `accent`, `neutral` or `prism`. */
  tint: string;
  /** For `pattern: "glyph"`: which mark to draw. */
  glyph?: string;
}

export const allCosmetics: Cosmetic[] = rawCosmetics as Cosmetic[];

const index = new Map<string, Cosmetic>();
for (const c of allCosmetics) index.set(c.id, c);

/** The kinds shown as store categories, in display order. */
export const COSMETIC_KINDS: CosmeticKind[] = ['sleeve', 'playmat', 'avatar', 'banner', 'misc'];

export const COSMETIC_KIND_LABEL: Record<CosmeticKind, string> = {
  sleeve: 'Card sleeves',
  playmat: 'Playmats',
  avatar: 'Avatars',
  banner: 'Banners',
  misc: 'Cosmetics',
};

export function cosmeticsOfKind(kind: CosmeticKind): Cosmetic[] {
  return allCosmetics.filter((c) => c.kind === kind);
}

export function defaultCosmeticId(kind: CosmeticKind): string {
  const found = allCosmetics.find((c) => c.kind === kind && c.default === true);
  return found?.id ?? `${kind}-default`;
}

/**
 * Look a cosmetic up by id. The profile store shipped with the literal string
 * "default" in each slot before cosmetics existed, so that value resolves to
 * the kind's free entry rather than to nothing.
 */
export function getCosmetic(id: string, kind?: CosmeticKind): Cosmetic | undefined {
  if (id === 'default' && kind) return index.get(defaultCosmeticId(kind));
  return index.get(id);
}

/** Is this slot filled with something other than the free starter? */
export function isEquippedNonDefault(id: string, kind: CosmeticKind): boolean {
  const cosmetic = getCosmetic(id, kind);
  return cosmetic !== undefined && cosmetic.default !== true;
}
